/**
 * TIẾN TRÌNH NGƯỜI CHƠI (meta) — thuần logic, lưu được ra JSON.
 * Gồm: vàng, tướng đã mua, phòng tập luyện theo class, phòng thời gian theo tướng,
 * phụ kiện đã mua + đang mặc.
 */
import type { ClassId, Stats } from './types';
import { CLASSES, CLASS_ORDER } from '../data/classes';
import { POTIONS, type PotionKind } from '../data/items';
import { defenderPrice, trainingCost, trainingGain, TIME_ROOM_POWER_PER_GOLD, powerCapForStage } from './formulas';
import { ensureProgression, type CraftState } from './craft';
import type { ProgressState } from './progress';
import { FIRST_MAP_ID, type Difficulty } from '../data/maps';
import { createArenaProgress, type ArenaProgress } from './arena-meta';

/**
 * PHIÊN BẢN SAVE.
 * v4: thêm tiến trình màn (clearedMaps · currentMap · currentDiff) + két đồ
 *     (kim cương · mảnh · chiêu đã ghép · 7 ô trang bị). `migrate()` tự vá save v3.
 * v5: thêm `formByClass` — tab TRANG PHỤC: dáng (ảnh 3×3 trong `art/skills/<class>/`) đang
 *     chọn cho từng class. Save cũ thiếu trường này thì `migrate()` vá thành `{}` (dùng dáng gốc).
 * v6: XOÁ HẲN PHỤ KIỆN (bỏ `ownedAccessories` + `loadout` của từng tướng) và ĐỔI LUẬT SỔ
 *     NHÂN VẬT: tối đa 5, tên nhân vật = tên class (xem `MAX_ROSTER`).
 * v8: thêm `arena` — TIẾN TRÌNH CHẾ ĐỘ ARENA (xem `ArenaProgress`, `src/sim/arena-meta.ts`).
 *     Arena là chế độ BỔ SUNG: save v7 thiếu trường này thì `migrateMeta()` vá thành mặc định,
 *     và MỌI trường của game TD (vàng/ class/ màn đã qua/ kim cương/ mảnh/ chiêu) GIỮ NGUYÊN.
 */
export const SAVE_VERSION = 8;

/**
 * SỔ NHÂN VẬT TỐI ĐA — ĐÚNG BẰNG SỐ TƯỚNG RA SÂN (`MAX_HEROES_ON_FIELD` trong `sim/world.ts`).
 *
 * Người dùng: *"game không có nhiều nhân vật, mỗi game chỉ được cho ra 5 nhân vật thuộc 5 class
 * (có thể mua nhiều nhân vật cùng class nếu như chưa max 5)… tên nhân vật chính là tên class
 * (cái mà đang đến ở màn hình home thậm chí lên 60 là sai)"*.
 *
 * ⚠️ LỖI CŨ: mỗi lần mua là nhét thêm 1 bản ghi VĨNH VIỄN vào `meta.heroes` (tên xoay vòng trong
 * danh sách 3 tên/ class) ⇒ chơi vài trận là badge trang chủ hiện **60**.
 * Nay: tên = tên class; sổ đầy 5 thì KHÔNG thêm bản ghi nữa (vẫn mua được cho trận này).
 */
export const MAX_ROSTER = 5;

/**
 * TIẾN TRÌNH ĐẦY ĐỦ.
 *
 * Kế thừa 2 interface của module thuần để LUẬT chỉ có MỘT BẢN:
 *   · `ProgressState` (sim/progress.ts): màn nào đã qua · kim cương · mảnh
 *   · `CraftState`    (sim/craft.ts)   : ghép mảnh · 7 ô chiêu · shop
 * Nhờ vậy `isUnlocked(meta, …)` hay `equipSkill(meta, …)` gọi thẳng được, không phải
 * bọc/ chuyển đổi — và không có chuyện UI tự viết lại luật mở khoá rồi lệch với sim.
 */
export interface MetaState extends ProgressState, CraftState {
  version: number;
  gold: number;
  /**
   * Màn đang chơi (chỉ số nền cho `buildWaves`). Giữ lại để tương thích save cũ và cho
   * `tools/balance.ts`; màn THẬT của người chơi nay là `currentMap` ('1.1' … '3.5').
   */
  stageIndex: number;
  /**
   * CLASS ĐÃ MỞ KHOÁ — thay cho SỔ TƯỚNG (`heroes[]`) đã bị XOÁ.
   *
   * Người dùng chốt: *"Xoá sổ tướng đi tính năng đó giờ không dùng nữa, sổ tướng tính theo số
   * lượng class đang active (class có thể mua trong trận)"*. Từ nay:
   *   · Nhân vật KHÔNG còn được lưu vĩnh viễn. Mua tướng = mua TRONG TRẬN, đếm theo
   *     `world.classHeroes` (tối đa `MAX_HEROES_ON_FIELD`) — anh chốt phương án 2a.
   *   · Thứ lưu vĩnh viễn là CLASS: class nào đã mở thì mua được tướng class đó mọi trận.
   */
  unlocked: ClassId[];
  /** MẢNH CLASS theo từng class (kể cả class CHƯA mở khoá — để cày dần mà mở). */
  classShards: Partial<Record<ClassId, number>>;
  /**
   * SỨC MẠNH PHÒNG THỜI GIAN theo CLASS (thay `HeroRecord.timePower` cũ).
   * Người dùng chốt: *"Sức mạnh trong phòng thời gian chuyển ra ngoài màn hình chính, gắn theo
   * class giống training"* ⇒ luyện `strength` thì MỌI tướng strength mạnh lên.
   */
  classPower: Partial<Record<ClassId, number>>;
  /** cấp phòng tập luyện theo class */
  training: Record<ClassId, number>;
  /**
   * DÁNG ĐANG MẶC THEO CLASS (tab Trang phục) — id dáng = slug tên file ảnh 3×3 trong
   * `art/skills/<class>/`. Không có khoá ⇒ dùng dáng gốc của class (`art/ai/<class>-parts.png`).
   */
  formByClass: Partial<Record<ClassId, string>>;
  potions: Record<PotionKind, number>;
  totalKills: number;
  bestWave: number;
  stagesCleared: number;
  /** màn đang chọn ('1.1' … '3.5') + độ khó đang chọn */
  currentMap: string;
  currentDiff: Difficulty;
  /**
   * TIẾN TRÌNH CHẾ ĐỘ ARENA (v8) — chế độ BỔ SUNG, KHÔNG thay thế game TD.
   *
   * ⚠️ VÌ SAO LÀ MỘT NHÓM RIÊNG CHỨ KHÔNG RẢI VÀO `MetaState`: toàn bộ state Arena nằm trong
   * `ArenaProgress` (xem `src/sim/arena-meta.ts`) nên `migrateMeta()` chỉ phải vá ĐÚNG MỘT trường,
   * và mọi luật riêng của Arena (ví riêng · cấp mỏ · tổng kết trận) có MỘT nhà. Người đọc save cũng
   * thấy ngay ranh giới giữa "tiến trình TD" và "tiến trình Arena".
   *
   * ⚠️ TÀI NGUYÊN ARENA TRONG ĐÂY **KHÔNG PHẢI** `meta.gold`: đó là VÍ RIÊNG (xem chú thích
   * `ArenaProgress.resource`). Đừng "gộp cho tiện" — gộp là phá cân bằng kinh tế của cả hai chế độ.
   */
  arena: ArenaProgress;
}

/**
 * STATE MỚI TINH cho người chơi lần đầu (hoặc save hỏng).
 *
 * ⚠️ v7: KHÔNG còn `heroes[]` (sổ tướng đã xoá). Cái lưu vĩnh viễn nay là:
 *   `unlocked` (class đã mở) · `classShards` (mảnh class) · `classPower` (sức mạnh phòng thời gian).
 * 5 class gốc mở sẵn để chơi được ngay; 2 class mới (Thợ mộc · Thợ lặn) phải gom `UNLOCK_SHARDS`.
 */
export function createMeta(): MetaState {
  const training = {} as Record<ClassId, number>;
  const classPower = {} as Partial<Record<ClassId, number>>;
  for (const c of CLASS_ORDER) {
    training[c] = 0;
    classPower[c] = 0;
  }
  const meta: MetaState = {
    version: SAVE_VERSION,
    gold: 260,
    stageIndex: 0,
    unlocked: [...CLASS_ORDER],
    classShards: {},
    classPower,
    training,
    // chưa chọn dáng nào ⇒ mọi class dùng dáng gốc (ảnh `art/ai/<class>-parts.png`)
    formByClass: {},
    potions: { hp: 2, mana: 2, stamina: 1 },
    totalKills: 0,
    bestWave: 0,
    stagesCleared: 0,
    // ---- tiến trình màn (v4)
    currentMap: FIRST_MAP_ID,
    currentDiff: 'easy',
    clearedMaps: [],
    diamonds: 0,
    shards: { hero: {}, skill: {} },
    // ---- ghép mảnh/ trang bị chiêu (v4) — `ensureProgression` dựng đủ 7 ô cho mọi class
    craftedSkills: {},
    skillLoadout: {},
    // ---- tiến trình ARENA (v8) — chế độ BỔ SUNG, ví riêng, không dính `gold`
    arena: createArenaProgress(),
  };
  ensureProgression(meta);
  return meta;
}

/**
 * MUA TƯỚNG — nay KHÔNG còn "sổ nhân vật", chỉ còn luật theo MÀN.
 *
 * Người dùng chốt: *"Xoá sổ tướng đi tính năng đó giờ không dùng nữa, sổ tướng tính theo số lượng
 * class đang active (class có thể mua trong trận)"* và *"2: (a) nhé"* (đếm = 5 tướng đang SỐNG
 * trên sân trong màn đó).
 *
 * Vì vậy hàm này KHÔNG ghi gì vào save nữa — nó chỉ:
 *   1. kiểm class đã mở khoá chưa (`meta.unlocked`),
 *   2. kiểm sân còn chỗ (`fieldedCount < MAX_HEROES_ON_FIELD`),
 *   3. kiểm đủ vàng, rồi TRỪ vàng.
 * Ai ra sân là việc của `Game.doBuyHero()` (gọi `world.addHeroToBattle`).
 */
export function buyHero(
  meta: MetaState,
  classId: ClassId,
  ownedInRun?: number,
  /**
   * `fieldedCount` = SỐ TƯỚNG ĐANG SỐNG TRÊN SÂN trong màn này (người dùng chốt phương án 2a).
   * Ở trang chủ truyền 0 (không có màn nào đang chạy). Không truyền ⇒ coi như 0.
   */
  ctx: { fieldedCount?: number } = {},
): { ok: boolean; message: string } {
  if (!meta.unlocked.includes(classId)) {
    const need = UNLOCK_SHARDS;
    const have = meta.classShards[classId] ?? 0;
    return {
      ok: false,
      message: `Chưa mở khoá ${CLASSES[classId]?.name ?? classId} — cần ${need} mảnh (đang có ${have})`,
    };
  }
  const fielded = ctx.fieldedCount ?? 0;
  if (fielded >= MAX_ROSTER) {
    return { ok: false, message: `Sân đã đủ ${MAX_ROSTER} tướng — đợi tướng gục hoặc qua màn` };
  }
  const price = heroPrice(meta, classId, ownedInRun);
  if (meta.gold < price) return { ok: false, message: `Không đủ vàng (cần ${price})` };
  meta.gold -= price;
  return { ok: true, message: `${CLASSES[classId]?.name ?? classId} ra sân` };
}

/** SỐ MẢNH CLASS ĐỂ MỞ KHOÁ một class. (5 class gốc mở sẵn; 2 class mới cần gom đủ.) */
export const UNLOCK_SHARDS = 30;

/**
 * MỞ KHOÁ CLASS bằng mảnh (mảnh gom từ qua màn + mua ở Shop kim cương + bán lại ra vàng ở Túi đồ).
 * Tiêu đúng `UNLOCK_SHARDS` mảnh, KHÔNG trừ thêm vàng.
 */
export function unlockClassWithShards(meta: MetaState, classId: ClassId): { ok: boolean; message: string } {
  const name = CLASSES[classId]?.name ?? classId;
  if (meta.unlocked.includes(classId)) return { ok: false, message: `${name} đã mở khoá rồi` };
  const have = meta.classShards[classId] ?? 0;
  if (have < UNLOCK_SHARDS) {
    return { ok: false, message: `Thiếu mảnh ${name}: cần ${UNLOCK_SHARDS}, đang có ${have}` };
  }
  meta.classShards[classId] = have - UNLOCK_SHARDS;
  meta.unlocked.push(classId);
  return { ok: true, message: `Đã mở khoá ${name}` };
}

/**
 * NÂNG CẤP TƯỚNG BẰNG MẢNH CLASS (khi class ĐÃ mở khoá).
 *
 * Người dùng: *"nếu đã unlock và đủ mảnh, người dùng có thể dùng để nâng cấp tướng"*.
 * Mỗi lần nâng = +1 điểm `classPower` (mỗi điểm = +0,5% toàn bộ chỉ số — xem `classStatsFor`),
 * đúng cơ chế phòng thời gian cũ nhưng nay theo CLASS.
 */
export function upgradeClassWithShards(meta: MetaState, classId: ClassId): { ok: boolean; message: string } {
  const name = CLASSES[classId]?.name ?? classId;
  if (!meta.unlocked.includes(classId)) return { ok: false, message: `Chưa mở khoá ${name}` };
  const cost = UPGRADE_SHARDS;
  const have = meta.classShards[classId] ?? 0;
  if (have < cost) return { ok: false, message: `Thiếu mảnh ${name}: cần ${cost}, đang có ${have}` };
  meta.classShards[classId] = have - cost;
  meta.classPower[classId] = (meta.classPower[classId] ?? 0) + 1;
  return { ok: true, message: `${name} +1 sức mạnh (tổng ${meta.classPower[classId]})` };
}

/** SỐ MẢNH CLASS cho 1 lần nâng cấp tướng. */
export const UPGRADE_SHARDS = 10;

/**
 * BÁN MẢNH CLASS RA VÀNG — người dùng chốt giá **20 vàng/ mảnh**.
 *
 * ⚠️ Vì sao 20 (chứ không phải 100 như anh hỏi lúc đầu): đo được nguồn thu mảnh là 0,6–1,2 mảnh/
 * lượt qua màn ⇒ bán 100 thì mỗi lượt màn 3.5 Elite ra 120 vàng, và 30 mảnh (mở class) = 3.000
 * vàng ≈ 15 lần mua tướng trong trận — biến mảnh thành máy in vàng và tự phá mục tiêu "mảnh để
 * mở class". 20 vàng/ mảnh ⇒ 30 mảnh = 600 vàng, vẫn đáng bán khi dư nhưng không phá tiến trình.
 */
export const SHARD_SELL_GOLD = 20;

/** Bán `count` mảnh của một class. Trả về số vàng nhận được. */
export function sellClassShards(meta: MetaState, classId: ClassId, count: number): { ok: boolean; message: string; gold?: number } {
  const n = Math.max(0, Math.floor(count));
  const have = meta.classShards[classId] ?? 0;
  if (n <= 0) return { ok: false, message: 'Số mảnh phải lớn hơn 0' };
  if (have < n) return { ok: false, message: `Không đủ mảnh ${CLASSES[classId]?.name ?? classId} (đang có ${have})` };
  const gold = n * SHARD_SELL_GOLD;
  meta.classShards[classId] = have - n;
  meta.gold += gold;
  return { ok: true, message: `Bán ${n} mảnh được ${gold} vàng`, gold };
}

/** Id nhân vật mới — duy nhất trong sổ (thời gian + ngẫu nhiên, đủ cho save LOCAL). */
function nextHeroId(classId: ClassId): string {
  return `${classId}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4)}`;
}

/**
 * Giá một tướng.
 *
 * `owned` = số nhân vật cùng class ĐÃ CÓ. Mặc định lấy từ `meta.heroes` (sổ nhân vật ở trang chủ,
 * tối đa `MAX_ROSTER`); truyền `ownedOverride` để tính theo số đã mua TRONG TRẬN.
 * Nhân vật đầu tiên của mỗi class giá 200, mua thêm cùng class thì đắt dần.
 */
/**
 * GIÁ MUA TƯỚNG.
 *
 * ⚠️ ĐỔI Ở v7: trước tính theo số nhân vật CÙNG CLASS trong sổ (`meta.heroes`). Sổ đã xoá ⇒ nay
 * tính theo `ownedOverride` (số tướng cùng class bạn ĐÃ MUA TRONG MÀN NÀY), mặc định 0. Đúng luật
 * người dùng chốt: *"khi bắt đầu vào trận, chưa có tướng, tất cả các tướng đều giá 200"*.
 */
export function heroPrice(_meta: MetaState, _classId: ClassId, ownedOverride = 0): number {
  const owned = Math.max(0, ownedOverride);
  return Math.round(defenderPrice(owned + 1) * 4 + owned * 30);
}

// ------------------------------------------------------------------ phòng tập luyện

export function trainCost(meta: MetaState, classId: ClassId): number {
  return trainingCost(CLASSES[classId].trainingBaseCost, meta.training[classId]);
}

export function trainClass(meta: MetaState, classId: ClassId): { ok: boolean; message: string } {
  const cost = trainCost(meta, classId);
  if (meta.gold < cost) return { ok: false, message: `Không đủ vàng (cần ${cost})` };
  meta.gold -= cost;
  meta.training[classId] += 1;
  return { ok: true, message: `${CLASSES[classId].name} lên cấp tập ${meta.training[classId]}` };
}

// ------------------------------------------------------------------ phòng thời gian

/**
 * PHÒNG THỜI GIAN — nay gắn theo CLASS (người dùng chốt: *"Sức mạnh trong phòng thời gian chuyển ra
 * ngoài màn hình chính, gắn theo class giống training"*). Trước đây phiên luyện gắn `heroId` của
 * một nhân vật trong sổ; sổ đã xoá nên trường đổi thành `classId`.
 */
export interface TimeRoomSession {
  classId: ClassId;
  secondsLeft: number;
  spentGold: number;
  powerGained: number;
  running: boolean;
  done: boolean;
  message: string;
}

export function startTimeRoom(meta: MetaState, classId: ClassId, seconds: number): TimeRoomSession {
  if (!meta.unlocked.includes(classId)) {
    return { classId, secondsLeft: 0, spentGold: 0, powerGained: 0, running: false, done: true, message: 'Class chưa mở khoá' };
  }
  return { classId, secondsLeft: seconds, spentGold: 0, powerGained: 0, running: true, done: false, message: 'Bắt đầu tập' };
}

/**
 * Tiến 1 bước phòng thời gian: tốn vàng theo giây, hết vàng thì TỰ DỪNG (đúng ý bạn).
 * @param dt giây
 */
export function stepTimeRoom(meta: MetaState, session: TimeRoomSession, dt: number): TimeRoomSession {
  if (!session.running || session.done) return session;
  const classId = session.classId;

  const cost = dt * 10; // vàng/giây
  if (meta.gold < cost) {
    return { ...session, running: false, done: true, message: 'Hết vàng — dừng tập luyện' };
  }
  meta.gold -= cost;
  const power = cost * TIME_ROOM_POWER_PER_GOLD;
  // sức mạnh gắn theo CLASS (không còn theo từng nhân vật)
  meta.classPower[classId] = (meta.classPower[classId] ?? 0) + power;

  const secondsLeft = Math.max(0, session.secondsLeft - dt);
  const done = secondsLeft <= 0;
  return {
    ...session,
    secondsLeft,
    spentGold: session.spentGold + cost,
    powerGained: session.powerGained + power,
    running: !done,
    done,
    message: done ? 'Xong thời gian đã chọn' : session.message,
  };
}


// ------------------------------------------------------------------ chỉ số hiệu dụng

/** Gom mọi nguồn sức mạnh thành chỉ số cuối của 1 tướng. */
/**
 * CHỈ SỐ HIỆU DỤNG CỦA MỘT CLASS.
 *
 * ⚠️ ĐỔI CHỮ KÝ (v7): trước nhận `HeroRecord` (một nhân vật trong sổ), nay nhận **classId** — vì
 * SỔ TƯỚNG ĐÃ XOÁ. Người dùng chốt sức mạnh phòng thời gian *"gắn theo class giống training"*,
 * nên mọi tướng cùng class dùng chung một bộ chỉ số.
 */
export function classStatsFor(classId: ClassId, meta: MetaState): Stats {
  const def = CLASSES[classId];
  const base: Stats = { ...def.base };

  /*
   * 1) PHÒNG TẬP LUYỆN THEO CLASS — NÂNG CẤP VĨNH VIỄN, KHÔNG PHỤ THUỘC MÀN.
   *
   * Yêu cầu người dùng: "việc nâng cấp tập luyện sẽ khiến class tăng chỉ số, KHÔNG phụ thuộc
   * vào màn".
   *
   * ⚠️ LỖI CŨ: phần cộng của tập luyện bị nhân bởi `powerCapForStage(stageIndex)` ở bước 4
   * (trần theo tiến trình màn). Nghĩa là cùng một cấp tập, chơi màn 1.1 thì yếu còn màn 3.5
   * thì mạnh — chỉ số của class phụ thuộc vào MÀN ĐANG CHỌN. Nay tách hẳn: tập luyện cho
   * đúng bằng `trainingGain` (lợi suất giảm dần, xem `sim/formulas.ts`), cộng thẳng vào base.
   */
  const level = meta.training[classId] ?? 0;
  const trained: Partial<Record<keyof Stats, number>> = {};
  for (const [k, v] of Object.entries(def.growth) as Array<[keyof Stats, number]>) {
    const gain = trainingGain(v, level) * level;
    base[k] += gain;
    trained[k] = gain;
  }

  // 2) phòng thời gian — mỗi điểm sức mạnh = +0.5% toàn bộ chỉ số chính
  const powerMult = 1 + (meta.classPower[classId] ?? 0) * 0.005;

  // 3) trần theo tiến trình màn (chống power creep vô hạn)
  const cap = powerCapForStage(meta.stageIndex);
  const totalPower = Math.min(powerMult, cap * 2.5);

  /*
   * ⚠️ TRẦN CHỈ NHÂN PHẦN "THEO MÀN" (phòng thời gian), KHÔNG nhân phần tập luyện.
   * Muốn trần thì bỏ hẳn phần tập luyện ra rồi cộng lại SAU — nếu nhân cả cục `base` thì tập
   * luyện lại bị phụ thuộc màn như lỗi cũ.
   */
  for (const key of Object.keys(base) as Array<keyof Stats>) {
    const trainPart = trained[key] ?? 0;
    const rest = base[key] - trainPart;
    base[key] = rest * totalPower + trainPart;
  }
  // chỉ số tỉ lệ không được vượt trần
  base.dodge = Math.min(0.7, base.dodge);
  base.crit = Math.min(0.85, base.crit);
  return base;
}

/** Sức mạnh tổng hợp để hiển thị (dùng cho UI so sánh). */
export function powerScore(stats: Stats): number {
  return Math.round(
    stats.hp * 0.1 + stats.atk * 3 + stats.def * 2 + stats.atkSpeed * 40 + stats.dodge * 300 + stats.crit * 200,
  );
}

export function potionPrice(kind: PotionKind): number {
  return POTIONS[kind].price;
}

export function buyPotion(meta: MetaState, kind: PotionKind, count = 1): { ok: boolean; message: string } {
  const cost = potionPrice(kind) * count;
  if (meta.gold < cost) return { ok: false, message: `Không đủ vàng (cần ${cost})` };
  meta.gold -= cost;
  meta.potions[kind] += count;
  return { ok: true, message: `Mua ${count} ${POTIONS[kind].name}` };
}
